/*
 * Copyright (c)  CYBERKOALA LLC 2025.
 * Proprietary License.
 * Redistribution and use in source and binary forms, with or without
 * modification, are restricted.
 */

import React from 'react';
import { getTracerModules } from '@apptracer/sdk';

type ErrorBoundaryState = {
  hasError: boolean;
  error?: Error;
};

export class AppErrorBoundary extends React.Component<React.PropsWithChildren, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };
  tracerLog = getTracerModules().log;

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Centralized logging hook (Sentry, Datadog, etc.)
    try {
      this.tracerLog?.log(error.message);
    } catch (err) {
      console.error('Tracer log failed:', err);
      //
    }
    console.error('Unhandled UI error:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex h-dvh w-dvw flex-col items-center justify-center bg-white dark:bg-[#1e1f22]">
          <h1 className="text-xl font-semibold">Something went wrong</h1>
          <p className="mt-2 text-sm opacity-70">
            The application encountered an unexpected error.
          </p>

          <button
            className="mt-6 rounded bg-black px-4 py-2 text-white dark:bg-white dark:text-black"
            onClick={() => window.location.reload()}
          >
            Reload application
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
